"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.FocusTrap = void 0;
var _react = _interopRequireWildcard(require("react"));
var _overlays = require("@cbhq/cds-common/tokens/overlays");
var _debounce = require("@cbhq/cds-common/utils/debounce");
var _browser = require("../utils/browser");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
const DEBOUNCE_MS = 50;
const NAVIGATION_KEYS = ['Tab', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
const ALPHABET_KEYS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'];
const maybeYubikeyString = str => /^(cc|vv)/.test(str);
const FocusTrap = /*#__PURE__*/(0, _react.memo)(function FocusTrap({
  children,
  onEscPress,
  disableTypeFocus,
  disableFocusTrap,
  disableAutoFocus
}) {
  const isFocused = (0, _react.useRef)(false);
  const childrenRef = (0, _react.useRef)(null);

  /** START KEYSTROKE HISTORY
   * Track keystrokes with a short memory */
  const keystrokeHistory = (0, _react.useRef)('');
  const debouncedKeystrokeReset = (0, _react.useMemo)(() => (0, _debounce.debounce)(() => {
    keystrokeHistory.current = '';
  }, DEBOUNCE_MS), []);
  const updateKeystrokeHistory = (0, _react.useCallback)(key => {
    // Update the keystroke history
    keystrokeHistory.current = keystrokeHistory.current.concat(key);
    debouncedKeystrokeReset();
  }, [debouncedKeystrokeReset]);
  /** END KEYSTROKE HISTORY */

  /** START FOCUS STATE HISTORY
   * Track focus state so whe know where to return to after yubikey input */
  const focusHistory = (0, _react.useRef)();
  const debouncedFocusReset = (0, _react.useMemo)(() => (0, _debounce.debounce)(() => {
    if (maybeYubikeyString(keystrokeHistory.current) && focusHistory.current) {
      focusHistory.current.focus();
      focusHistory.current = undefined;
    }
  }, DEBOUNCE_MS), []);
  const resetFocus = (0, _react.useCallback)(() => {
    var _getBrowserGlobals;
    // Update the keystroke history
    const document = (_getBrowserGlobals = (0, _browser.getBrowserGlobals)()) === null || _getBrowserGlobals === void 0 ? void 0 : _getBrowserGlobals.document;
    const activeElement = document === null || document === void 0 ? void 0 : document.activeElement;
    // Don't track the body (aka blur)
    if (activeElement !== (document === null || document === void 0 ? void 0 : document.body)) {
      focusHistory.current = activeElement;
    }
    // We need to blur to prevent the "enter" event from triggering something in the modal
    activeElement.blur();

    // Return to whence ye came
    debouncedFocusReset();
  }, [debouncedFocusReset]);
  /** END FOCUS STATE HISTORY */

  // trap focus for accessibility
  const handleKeyboardNavigation = (0, _react.useCallback)((event, element) => {
    var _getBrowserGlobals2;
    const document = (_getBrowserGlobals2 = (0, _browser.getBrowserGlobals)()) === null || _getBrowserGlobals2 === void 0 ? void 0 : _getBrowserGlobals2.document;
    const activeElement = document === null || document === void 0 ? void 0 : document.activeElement;
    if (!element || !document) return;
    const focusableElements = element.querySelectorAll(_overlays.FOCUSABLE_ELEMENTS);
    const menuItems = element.querySelectorAll('[role="menuitem"]');
    const isMenuItem = activeElement && Array.from(menuItems).includes(activeElement);
    if (focusableElements.length === 0) return;
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElementIndex = activeElement ? Array.from(focusableElements).indexOf(activeElement) : undefined;

    /** BAIL ON ENTRIES THAT LOOK LIKE YUBIKEY STRINGS
     * @link https://support.yubico.com/hc/en-us/articles/360013712639--Testing-Yubico-OTP
     */
    if (maybeYubikeyString(keystrokeHistory.current) && activeElement) {
      resetFocus();
      return;
    }

    // bring focus inside modal
    if (!isFocused.current &&
    // check if focus is inside modal
    !Array.from(focusableElements).includes(activeElement)) {
      // don't focus if arrow keys are used, instead allow scroll via keyboard
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        return;
      }
      firstElement.focus();
      isFocused.current = true;
      event.preventDefault();
    }
    if (event.key === 'Tab') {
      event.preventDefault();
    }
    if (isMenuItem && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      event.preventDefault();
    }
    const focusPrevElement = () => {
      if (activeElementIndex) {
        const previousElement = focusableElements[activeElementIndex - 1];
        previousElement.focus();
      }
    };

    // force active element to loop back to beginning of list
    const handleLastElement = () => {
      if (!event.shiftKey && event.key === 'Tab' || event.key === 'ArrowDown' && isMenuItem) {
        firstElement.focus();
        return;
      }
      if (event.key === 'ArrowUp' && isMenuItem || event.shiftKey && event.key === 'Tab') {
        focusPrevElement();
      }
    };
    const handleFirstElement = () => {
      // force active element to loop back to end of list
      if (event.shiftKey && event.key === 'Tab' || event.key === 'ArrowUp' && isMenuItem) {
        lastElement.focus();
        return;
      }
      if (event.key === 'ArrowDown' && isMenuItem || event.key === 'Tab') {
        focusableElements[1].focus();
      }
    };
    if (event.key === 'Home') {
      event.preventDefault();
      firstElement.focus();
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      lastElement.focus();
      return;
    }
    if (!disableTypeFocus && isMenuItem && ALPHABET_KEYS.includes(event.key)) {
      event.preventDefault();
      const arrFocusableElements = Array.from(focusableElements);
      const elementWithMatchingFirstLetter = arrFocusableElements.find(el => {
        var _el$textContent, _el$textContent$at;
        const textContentFirstLetter = (_el$textContent = el.textContent) === null || _el$textContent === void 0 ? void 0 : (_el$textContent$at = _el$textContent.at(0)) === null || _el$textContent$at === void 0 ? void 0 : _el$textContent$at.toLowerCase();
        const eventKeyLowerCase = event.key.toLowerCase();
        return textContentFirstLetter === eventKeyLowerCase;
      });
      if (elementWithMatchingFirstLetter) {
        elementWithMatchingFirstLetter.focus();
      }
    }
    if (activeElement === lastElement) {
      handleLastElement();
      return;
    }
    if (activeElement === firstElement) {
      handleFirstElement();
      return;
    }
    if ((event.key === 'ArrowDown' && isMenuItem || event.key === 'Tab') && activeElementIndex) {
      const nextElement = focusableElements[activeElementIndex + 1];
      nextElement.focus();
    }
    if ((event.key === 'ArrowUp' && isMenuItem || event.key === 'Tab' && event.shiftKey) && activeElementIndex) {
      focusPrevElement();
    }
  }, [disableTypeFocus, resetFocus]);
  const handleKeyDown = (0, _react.useCallback)(event => {
    // Update the keystroke history
    updateKeystrokeHistory(event.key);
    if (!disableFocusTrap && (NAVIGATION_KEYS.includes(event.key) || ALPHABET_KEYS.includes(event.key))) {
      handleKeyboardNavigation(event, childrenRef.current);
    }

    // Swallow the event, in case someone is listening on the body.
    event.stopPropagation();
  }, [updateKeystrokeHistory, disableFocusTrap, handleKeyboardNavigation]);
  const handleKeyUp = (0, _react.useCallback)(event => {
    if (event.key === 'Escape') {
      void (onEscPress === null || onEscPress === void 0 ? void 0 : onEscPress());
      isFocused.current = false;
    }
  }, [onEscPress]);
  (0, _react.useEffect)(() => {
    var _getBrowserGlobals3, _getBrowserGlobals4;
    (_getBrowserGlobals3 = (0, _browser.getBrowserGlobals)()) === null || _getBrowserGlobals3 === void 0 ? void 0 : _getBrowserGlobals3.window.addEventListener('keydown', handleKeyDown);
    (_getBrowserGlobals4 = (0, _browser.getBrowserGlobals)()) === null || _getBrowserGlobals4 === void 0 ? void 0 : _getBrowserGlobals4.window.addEventListener('keyup', handleKeyUp);
    return () => {
      var _getBrowserGlobals5, _getBrowserGlobals6;
      (_getBrowserGlobals5 = (0, _browser.getBrowserGlobals)()) === null || _getBrowserGlobals5 === void 0 ? void 0 : _getBrowserGlobals5.window.removeEventListener('keydown', handleKeyDown);
      (_getBrowserGlobals6 = (0, _browser.getBrowserGlobals)()) === null || _getBrowserGlobals6 === void 0 ? void 0 : _getBrowserGlobals6.window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);
  (0, _react.useEffect)(() => {
    var _getBrowserGlobals7;
    const document = (_getBrowserGlobals7 = (0, _browser.getBrowserGlobals)()) === null || _getBrowserGlobals7 === void 0 ? void 0 : _getBrowserGlobals7.document;
    const elements = childrenRef.current;
    if (!document || !elements || disableAutoFocus) {
      return;
    }
    const focusableElements = elements.querySelectorAll(_overlays.FOCUSABLE_ELEMENTS);
    if (focusableElements !== null && focusableElements !== void 0 && focusableElements.length) {
      focusableElements[0].focus();
    }
  }, [disableAutoFocus]);

  // only works for single child
  const onlyChild = _react.default.Children.only(children);
  if (!onlyChild) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, children);
  }
  return /*#__PURE__*/_react.default.cloneElement(children, {
    ref: childrenRef
  });
});
exports.FocusTrap = FocusTrap;